/**
 * The live battle map.
 *
 * Two clients in one file, because they are the same board seen from two
 * sides. The GM's calls go through `request()` and carry a session. The seat
 * calls deliberately do not: a player opens their link with no account, so
 * those use a bare fetch that never touches the auth provider — a player
 * page that needs Clerk to have initialised is a player page that breaks for
 * the one person it exists for.
 */
import { request } from '@/api/client';

export type TerrainKind =
  | 'wall'
  | 'difficult'
  | 'hazard'
  | 'cover'
  | 'water'
  | 'feature';

export type Side = 'opposition' | 'ally' | 'party' | 'neutral';

export interface MapFeature {
  id: string;
  name: string;
  kind: TerrainKind;
  x: number;
  y: number;
  w: number;
  h: number;
  /** What it does, in rules terms. The reason terrain is on the map. */
  effect: string;
}

export interface MapToken {
  id: string;
  name: string;
  side: Side;
  x: number;
  y: number;
  /** GM-only. Stripped server-side before a player ever sees the payload. */
  invisible: boolean;
  /** Freeform GM state: "on fire", "prone", "enlarged". */
  tags: string[];
}

export interface MapState {
  width: number;
  height: number;
  notes: string;
  features: MapFeature[];
  tokens: MapToken[];
}

export interface MapSuggestion {
  id: string;
  summary: string;
  ops: Record<string, unknown>[];
}

export interface BattleMap {
  id: string;
  session_id: string;
  encounter_id: string | null;
  draft_json: MapState;
  published_json: MapState | null;
  revision: number;
  published_revision: number;
  suggestions: MapSuggestion[];
}

export interface Seat {
  id: string;
  key: string;
  label: string;
  token_id: string | null;
  last_seen_at: string | null;
}

export const openBattleMap = (encounterId: string) =>
  request<{ map: BattleMap; seats: Seat[] }>(
    `/api/encounters/${encounterId}/battlemap`,
    { method: 'POST' },
  );

export const getBattleMap = (mapId: string) =>
  request<{ map: BattleMap; seats: Seat[] }>(`/api/battlemaps/${mapId}`);

export const saveDraft = (
  mapId: string,
  state: MapState,
  expectedRevision: number,
) =>
  request<{ revision: number }>(`/api/battlemaps/${mapId}/draft`, {
    method: 'PUT',
    body: { state, expected_revision: expectedRevision },
  });

export const publishMap = (mapId: string) =>
  request<{ published_revision: number }>(`/api/battlemaps/${mapId}/publish`, {
    method: 'POST',
  });

export const revokeSeat = (mapId: string, seatId: string) =>
  request<{ revoked: true }>(
    `/api/battlemaps/${mapId}/seats/${encodeURIComponent(seatId)}`,
    { method: 'DELETE' },
  );

export const replaceSeat = (mapId: string, seatId: string) =>
  request<{ seat: Seat }>(
    `/api/battlemaps/${mapId}/seats/${encodeURIComponent(seatId)}/replace`,
    { method: 'POST' },
  );

export const resolveSuggestion = (
  mapId: string,
  suggestionId: string,
  apply: boolean,
) =>
  request<{ map: BattleMap }>(
    `/api/battlemaps/${mapId}/suggestions/${suggestionId}?apply=${apply}`,
    { method: 'POST' },
  );

/** The link a player opens. Same origin as the dashboard. */
export const seatUrl = (key: string) =>
  `${window.location.origin}/#/map/${key}`;

export interface SeatView {
  revision: number;
  unchanged: boolean;
  state?: MapState | null;
  label?: string;
  token_id?: string | null;
  waiting?: boolean;
}

export class SeatFetchError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'SeatFetchError';
  }
}

async function seatFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...options,
      headers: options.body ? { 'Content-Type': 'application/json' } : undefined,
    });
  } catch {
    throw new SeatFetchError(
      'We couldn’t reach the battle map. Check your connection and try again.',
      0,
    );
  }
  if (!response.ok) {
    throw new SeatFetchError(
      response.status === 404
        ? 'That link is not valid any more.'
        : response.status === 403
          ? 'You can only move your own token.'
          : response.status === 429
            ? 'The map is receiving a lot of updates. Wait a moment and try again.'
            : 'The battle map hit a temporary problem. Please try again.',
      response.status,
    );
  }
  return (await response.json()) as T;
}

export const fetchSeat = (key: string, since: number) =>
  seatFetch<SeatView>(`/api/seats/${key}?since=${since}`);

export const moveSeatToken = (
  key: string,
  tokenId: string,
  x: number,
  y: number,
) =>
  seatFetch<{ revision: number; state: MapState }>(`/api/seats/${key}/move`, {
    method: 'POST',
    body: JSON.stringify({ token_id: tokenId, x, y }),
  });
